import { useCallback, useMemo, useState, useEffect, useRef, type MouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import {
  deleteGeneration,
  getHistory,
  toggleFavorite,
  fetchImageProxy,
  type GenerationHistoryFilters,
  type GenerationStatus,
  type HistoryRecord,
} from '@/api/generation';
import { APP_NAME } from '@/config/brand';
import ShareSheet from '@/pages/generate/components/ShareSheet';
import { useAppConfig } from '@/hooks/useAppConfig';
import { saveGenerationImageToAlbum, type SaveGenerationImageStage } from '@/utils/saveGenerationImage';
import { track } from '@/utils/tracker';
import { useGenerationTasks } from '@/contexts/generationTaskStore';
import { pushEvent } from '@/hooks/useAnalytics';
import HistoryFilterSheet, {
  type HistoryFilterValue,
  type HistoryModelOption,
} from './HistoryFilterSheet';

interface DisplayRecord {
  id: string;
  imageUrl?: string;
  status: GenerationStatus;
  reasonCode?: string;
  prompt: string;
  styleName: string;
  ratio: string;
  createdAt: string;
  pointsCost: number;
  favorited: boolean;
  modelId?: string;
  quality?: string;
}

const HISTORY_PAGE_SIZE = 12;

function isCompleteRecord(item: DisplayRecord): boolean {
  return item.status === 'success' && !!item.imageUrl;
}

function statusIcon(status: GenerationStatus): string {
  if (status === 'failed' || status === 'timeout') return 'ri-error-warning-line';
  if (status === 'blocked') return 'ri-shield-flash-line';
  return 'ri-loader-4-line';
}

function statusTone(status: GenerationStatus): string {
  if (status === 'failed' || status === 'timeout') return 'text-red-500 bg-red-50';
  if (status === 'blocked') return 'text-amber-500 bg-amber-50';
  return 'text-purple-500 bg-purple-50';
}

function formatHistoryDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const normalized = value.replace('T', ' ').replace(/\.\d+(?:[+-]\d{2}:\d{2}|Z)?$/, '');
    return normalized.length >= 16 ? normalized.slice(0, 16) : value;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    parsed.getFullYear(),
    pad(parsed.getMonth() + 1),
    pad(parsed.getDate()),
  ].join('-') + ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function toDisplay(r: HistoryRecord): DisplayRecord {
  return {
    id: r.gen_id,
    imageUrl: r.thumbnail_url || r.image_url,
    status: r.status,
    reasonCode: r.reason_code,
    prompt: r.prompt,
    styleName: r.style_name,
    ratio: r.ratio,
    createdAt: r.created_at,
    pointsCost: r.points_cost,
    favorited: r.favorited ?? false,
    modelId: r.model_id,
    quality: r.quality,
  };
}

function getSaveErrorStage(err: unknown): SaveGenerationImageStage | 'unknown' {
  if (err instanceof Error && 'stage' in err && typeof err.stage === 'string') {
    return err.stage as SaveGenerationImageStage;
  }
  return 'unknown';
}

interface HistoryGridProps {
  onCountLoaded?: (total: number) => void;
  favoritedOnly?: boolean;
}

export default function HistoryGrid({ onCountLoaded, favoritedOnly }: HistoryGridProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const config = useAppConfig();
  const { tasks } = useGenerationTasks();
  const [records, setRecords] = useState<DisplayRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewItem, setPreviewItem] = useState<DisplayRecord | null>(null);
  const [favoritedMap, setFavoritedMap] = useState<Record<string, boolean>>({});
  const [copyTip, setCopyTip] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveTip, setSaveTip] = useState<'saving' | 'done' | 'failed' | null>(null);
  const [deleteTip, setDeleteTip] = useState<'failed' | null>(null);
  const [previewImageLoaded, setPreviewImageLoaded] = useState(false);
  const [modelFacets, setModelFacets] = useState<Array<{ model_id: string; count: number }>>([]);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const previewBlobRef = useRef<Blob | null>(null);
  const savingRef = useRef<string | null>(null);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);

  const statusFilter = searchParams.get('status') ?? 'all';
  const modelFilter = searchParams.get('model') ?? 'all';
  const rawPeriodFilter = searchParams.get('period');
  const periodFilter: HistoryFilterValue['period'] = (
    rawPeriodFilter === 'today' || rawPeriodFilter === '7d' || rawPeriodFilter === '30d'
      ? rawPeriodFilter
      : 'all'
  );
  const orderFilter = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const historyFilters = useMemo<GenerationHistoryFilters>(() => {
    const filters: GenerationHistoryFilters = { sortOrder: orderFilter };
    if (statusFilter === 'active' || statusFilter === 'success' || statusFilter === 'unsuccessful') {
      filters.statusGroup = statusFilter;
    }
    if (modelFilter !== 'all') filters.modelId = modelFilter;
    const days = periodFilter === '7d' ? 7 : periodFilter === '30d' ? 30 : null;
    if (days !== null) {
      const createdFrom = new Date();
      createdFrom.setDate(createdFrom.getDate() - days);
      filters.createdFrom = createdFrom.toISOString();
    }
    if (periodFilter === 'today') {
      const createdFrom = new Date();
      createdFrom.setHours(0, 0, 0, 0);
      filters.createdFrom = createdFrom.toISOString();
    }
    return filters;
  }, [modelFilter, orderFilter, periodFilter, statusFilter]);
  const historyFilterKey = JSON.stringify(historyFilters);
  const filterSheetValue = useMemo<HistoryFilterValue>(() => ({
    model: modelFilter,
    period: periodFilter,
    order: orderFilter,
  }), [modelFilter, orderFilter, periodFilter]);
  const secondaryFilterCount = Number(modelFilter !== 'all')
    + Number(periodFilter !== 'all')
    + Number(orderFilter !== 'desc');
  const hasAppliedFilters = statusFilter !== 'all' || secondaryFilterCount > 0;

  const updateFilter = (key: 'status' | 'model' | 'period' | 'order', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all' || (key === 'order' && value === 'desc')) next.delete(key);
    else next.set(key, value);
    next.set('tab', 'history');
    next.delete('gen_id');
    setSearchParams(next, { replace: true });
  };

  const closeFilterSheet = useCallback(() => {
    setFilterSheetOpen(false);
    window.requestAnimationFrame(() => filterTriggerRef.current?.focus());
  }, []);

  const openFilterSheet = () => {
    pushEvent('history_filter_open');
    setFilterSheetOpen(true);
  };

  const applyFilterSheet = useCallback((value: HistoryFilterValue) => {
    const next = new URLSearchParams(searchParams);
    if (value.model === 'all') next.delete('model');
    else next.set('model', value.model);
    if (value.period === 'all') next.delete('period');
    else next.set('period', value.period);
    if (value.order === 'desc') next.delete('order');
    else next.set('order', value.order);
    next.set('tab', 'history');
    next.delete('gen_id');
    pushEvent('history_filter_apply', undefined, {
      model: value.model,
      period: value.period,
      order: value.order,
    });
    setSearchParams(next, { replace: true });
    closeFilterSheet();
  }, [closeFilterSheet, searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    for (const key of ['status', 'model', 'period', 'order', 'gen_id']) next.delete(key);
    next.set('tab', 'history');
    pushEvent('history_filter_reset');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (favoritedOnly || tasks.length === 0) return;
    setRecords((current) => {
      const byId = new Map(current.map((record) => [record.id, record]));
      for (const task of tasks) {
        const statusMatches = !historyFilters.statusGroup
          || (historyFilters.statusGroup === 'active' && ['created', 'queued', 'generating', 'auditing'].includes(task.status))
          || (historyFilters.statusGroup === 'success' && task.status === 'success')
          || (historyFilters.statusGroup === 'unsuccessful' && ['failed', 'blocked', 'timeout'].includes(task.status));
        const modelMatches = !historyFilters.modelId || task.model_id === historyFilters.modelId;
        const timeMatches = !historyFilters.createdFrom
          || new Date(task.created_at).getTime() >= new Date(historyFilters.createdFrom).getTime();
        if (!statusMatches || !modelMatches || !timeMatches) continue;
        const previous = byId.get(task.gen_id);
        byId.set(task.gen_id, {
          id: task.gen_id,
          imageUrl: task.thumbnail_url || task.image_url || previous?.imageUrl,
          status: task.status,
          reasonCode: task.reason_code,
          prompt: task.prompt,
          styleName: previous?.styleName ?? task.style_id ?? '',
          ratio: task.ratio,
          createdAt: previous?.createdAt ?? task.created_at,
          pointsCost: task.points_cost,
          favorited: previous?.favorited ?? false,
          modelId: task.model_id,
          quality: task.quality,
        });
      }
      return Array.from(byId.values()).sort((a, b) => (
        orderFilter === 'asc'
          ? a.createdAt.localeCompare(b.createdAt)
          : b.createdAt.localeCompare(a.createdAt)
      ));
    });
  }, [favoritedOnly, historyFilterKey, historyFilters, orderFilter, tasks]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setRecords([]);
    setFavoritedMap({});
    getHistory(1, HISTORY_PAGE_SIZE, favoritedOnly, favoritedOnly ? {} : historyFilters)
      .then((res) => {
        if (cancelled) return;
        const items = res.items.map(toDisplay);
        setRecords(items);
        setTotal(res.total);
        setHasMore(res.has_more);
        setPage(res.page);
        setModelFacets(res.facets?.models ?? []);
        onCountLoaded?.(res.total);
      })
      .catch(() => {
        if (cancelled) return;
        setRecords([]);
        setTotal(0);
        setHasMore(false);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritedOnly, historyFilterKey, reloadNonce]);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    getHistory(nextPage, HISTORY_PAGE_SIZE, favoritedOnly, favoritedOnly ? {} : historyFilters)
      .then((res) => {
        const items = res.items.map(toDisplay);
        setRecords((prev) => [...prev, ...items]);
        setTotal(res.total);
        setHasMore(res.has_more);
        setPage(res.page);
        setModelFacets(res.facets?.models ?? []);
        onCountLoaded?.(res.total);
      })
      .catch(() => {
        // silently ignore load-more errors
      })
      .finally(() => {
        setLoadingMore(false);
      });
  };

  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopyTip(true);
    setTimeout(() => setCopyTip(false), 2000);
  };

  const handleSaveToAlbum = async (url: string, id: string) => {
    if (savingRef.current) return;
    savingRef.current = id;
    setSavingId(id);
    setSaveTip('saving');
    const fileName = `glowpic_${id}_${Date.now()}.jpg`;

    const finish = (status: 'done' | 'failed') => {
      savingRef.current = null;
      setSavingId(null);
      setSaveTip(status);
      setTimeout(() => setSaveTip(null), 2000);
    };

    // iOS native app: save the original generation image directly to Photos.
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      try {
        const blob = await saveGenerationImageToAlbum({ genId: id, imageUrl: url, quality: 'sd' });
        previewBlobRef.current = blob;
        track.imageSaveSuccess({
          source: 'history_detail',
          genId: id,
          quality: 'sd',
          method: blob.saveMethod,
          stage: blob.saveStage,
          durationMs: blob.saveDurationMs,
          bytes: blob.saveBytes,
          httpStatus: blob.saveHttpStatus,
          urlType: blob.saveUrlType,
        });
        finish('done');
      } catch (err) {
        const stage = getSaveErrorStage(err);
        console.warn('history image save failed', {
          genId: id,
          stage,
          error: err,
        });
        track.imageSaveFail({ source: 'history_detail', genId: id, stage, quality: 'sd' });
        finish('failed');
      }
      return;
    }

    // Web: download via blob URL or share sheet
    const doSave = async (blob: Blob) => {
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: APP_NAME });
        } catch {
          // user cancelled — no-op
        }
      } else {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    };

    const cached = previewBlobRef.current;
    if (cached) {
      const cameFromDownloadUrl = 'saveUrlType' in cached || 'saveMethod' in cached;
      if (cameFromDownloadUrl) {
        try {
          await doSave(cached);
          finish('done');
        } catch {
          finish('failed');
        }
        return;
      }
    }

    try {
      const blob = await saveGenerationImageToAlbum({ genId: id, imageUrl: url, quality: 'sd' });
      previewBlobRef.current = blob;
      await doSave(blob);
      finish('done');
    } catch {
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        finish('done');
      } catch {
        finish('failed');
      }
    }
  };

  useEffect(() => {
    setPreviewImageLoaded(false);
    previewBlobRef.current = null;
    if (!previewItem) return;
    let cancelled = false;
    if (!previewItem.imageUrl) return;
    fetchImageProxy(previewItem.imageUrl)
      .then(blob => { if (!cancelled) previewBlobRef.current = blob; })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewItem?.id]);

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteGeneration(id);
      setRecords((prev) => prev.filter((r) => r.id !== id));
      setTotal((prev) => {
        const next = Math.max(0, prev - 1);
        onCountLoaded?.(next);
        return next;
      });
      setPreviewItem(null);
    } catch {
      setDeleteTip('failed');
      setTimeout(() => setDeleteTip(null), 2000);
    }
  };

  const handleToggleFavorite = async (genId: string, currentFav: boolean, e: MouseEvent) => {
    e.stopPropagation();
    setFavoritedMap(prev => ({ ...prev, [genId]: !currentFav }));
    try {
      await toggleFavorite(genId, !currentFav);
    } catch {
      setFavoritedMap(prev => ({ ...prev, [genId]: currentFav }));
    }
  };

  const getModelDisplayName = (modelId?: string): string => {
    if (!modelId) return '';
    const model = config?.models?.find((item) => item.id === modelId);
    return model?.name_zh ?? model?.name ?? modelId;
  };

  useEffect(() => {
    const requestedGenId = searchParams.get('gen_id');
    if (!requestedGenId || previewItem?.id === requestedGenId) return;
    const requested = records.find((item) => item.id === requestedGenId);
    if (requested && isCompleteRecord(requested)) setPreviewItem(requested);
  }, [previewItem?.id, records, searchParams]);

  const modelOptions: HistoryModelOption[] = modelFacets.map((facet) => ({
    id: facet.model_id,
    label: getModelDisplayName(facet.model_id),
    count: facet.count,
  }));

  const filterControls = !favoritedOnly ? (
    <>
      <div className="px-4 pb-3" data-testid="history-filters">
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-1 scrollbar-none">
            {([
              ['all', t('profile_filterStatusAll', '全部')],
              ['active', t('profile_filterStatusActive', '进行中')],
              ['success', t('profile_filterStatusSuccess', '成功')],
              ['unsuccessful', t('profile_filterStatusIncomplete', '未完成')],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={statusFilter === value}
                onClick={() => {
                  pushEvent('history_status_filter', undefined, { status: value });
                  updateFilter('status', value);
                }}
                className={`min-h-9 shrink-0 rounded-full px-3 text-xs font-semibold ${
                  statusFilter === value
                    ? 'bg-gradient-to-r from-[#FF6B9D] to-[#A855F7] text-white'
                    : 'bg-white text-gray-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            ref={filterTriggerRef}
            type="button"
            aria-label={t('profile_filterButton', '筛选')}
            aria-haspopup="dialog"
            aria-expanded={filterSheetOpen}
            onClick={openFilterSheet}
            className={`relative flex min-h-10 shrink-0 items-center gap-1 rounded-full border px-3 text-xs font-semibold ${
              secondaryFilterCount > 0
                ? 'border-purple-200 bg-purple-50 text-purple-600'
                : 'border-gray-100 bg-white text-gray-600'
            }`}
          >
            <i className="ri-equalizer-2-line text-sm" />
            <span>{t('profile_filterButton', '筛选')}</span>
            {secondaryFilterCount > 0 && (
              <span
                data-testid="history-filter-count"
                className="flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-1 text-[9px] text-white"
              >
                {secondaryFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>
      <HistoryFilterSheet
        isOpen={filterSheetOpen}
        value={filterSheetValue}
        modelOptions={modelOptions}
        loading={loading && modelFacets.length === 0}
        onClose={closeFilterSheet}
        onApply={applyFilterSheet}
      />
    </>
  ) : null;

  if (loading) {
    return (
      <>
        {filterControls}
        <div className="flex flex-col items-center justify-center py-16 px-5">
          <div className="w-8 h-8 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        {filterControls}
        <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50">
            <i className="ri-wifi-off-line text-2xl text-red-400" />
          </div>
          <p className="mb-1 text-sm font-semibold text-gray-600">
            {t('profile_filterLoadFailed', '生成记录加载失败')}
          </p>
          <p className="mb-5 text-xs text-gray-400">
            {t('profile_filterLoadFailedDesc', '请检查网络后重试')}
          </p>
          <button
            type="button"
            onClick={() => setReloadNonce((current) => current + 1)}
            className="min-h-11 rounded-full bg-purple-50 px-6 text-sm font-semibold text-purple-600 active:bg-purple-100"
          >
            {t('profile_filterRetry', '重新加载')}
          </button>
        </div>
      </>
    );
  }

  if (total === 0) {
    if (favoritedOnly) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, #FFE4EF, #F5EEFF)' }}>
            <i className="ri-star-line text-pink-400 text-2xl"></i>
          </div>
          <p className="text-sm font-semibold text-gray-600 mb-1">{t('profile_noFavorites')}</p>
          <p className="text-xs text-gray-400 mb-5">{t('profile_noFavoritesDesc')}</p>
        </div>
      );
    }
    if (hasAppliedFilters) {
      return (
        <>
          {filterControls}
          <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
            <div
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl"
              style={{ background: 'linear-gradient(135deg, #FFE4EF, #F5EEFF)' }}
            >
              <i className="ri-filter-off-line text-2xl text-purple-400" />
            </div>
            <p className="mb-1 text-sm font-semibold text-gray-600">
              {t('profile_filterNoMatches', '没有符合条件的生成记录')}
            </p>
            <p className="mb-5 text-xs text-gray-400">
              {t('profile_filterNoMatchesDesc', '调整筛选条件后再试试')}
            </p>
            <button
              type="button"
              onClick={clearAllFilters}
              className="min-h-11 rounded-full bg-purple-50 px-6 text-sm font-semibold text-purple-600 active:bg-purple-100"
            >
              {t('profile_filterClear', '清除筛选')}
            </button>
          </div>
        </>
      );
    }
    return (
      <>
        {filterControls}
        <div className="flex flex-col items-center justify-center py-16 px-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, #FFE4EF, #F5EEFF)' }}>
            <i className="ri-image-line text-pink-400 text-2xl"></i>
          </div>
          <p className="text-sm font-semibold text-gray-600 mb-1">{t('profile_noHistory')}</p>
          <p className="text-xs text-gray-400 mb-5">{t('profile_noHistoryDesc')}</p>
          <button
            onClick={() => navigate('/generate')}
            className="px-6 py-2.5 rounded-full text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
          >
            {t('profile_goGenerate')}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {filterControls}
      {/* 固定方形双列网格 */}
      <div className="grid grid-cols-2 gap-3 px-4 pb-6">
        {records.map((item) => {
          const isFav = favoritedMap[item.id] ?? item.favorited;
          const complete = isCompleteRecord(item);
          const statusLabel = t(`gen_status_${item.status}`, item.status);
          return (
            <div
              role="button"
              key={item.id}
              data-testid={`history-generation-${item.id}`}
              data-generation-id={item.id}
              aria-label={`history-generation-${item.id}`}
              className={`min-w-0 overflow-hidden rounded-2xl bg-white transition-transform ${complete ? 'cursor-pointer active:scale-95' : ''}`}
              onClick={() => { if (complete) setPreviewItem(item); }}
            >
              <span
                role="status"
                className="sr-only"
                data-testid={`history-generation-terminal-${item.id}`}
                aria-label={`history-generation-${item.id}-${item.status}-${item.reasonCode ?? 'none'}`}
              />
              <div className="relative aspect-square">
                {complete ? (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-100 animate-pulse rounded-t-2xl" />
                    <img
                      src={item.imageUrl || ''}
                      alt={`${getModelDisplayName(item.modelId) || 'AI'} 生成图片`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover object-top opacity-0 transition-opacity duration-500"
                      onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '1'; }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '1'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                    <button
                      className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer z-10 active:scale-90 transition-transform ${
                        isFav ? 'bg-gradient-to-br from-[#FF6B9D] to-[#A855F7]' : 'bg-black/25 backdrop-blur-sm'
                      }`}
                      onClick={(e) => handleToggleFavorite(item.id, isFav, e)}
                    >
                      <i className={`ri-star-${isFav ? 'fill' : 'line'} text-white text-sm`}></i>
                    </button>
                  </>
                ) : (
                  <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-t-2xl ${statusTone(item.status)}`}>
                    <i className={`${statusIcon(item.status)} text-2xl ${['queued', 'generating', 'auditing', 'created'].includes(item.status) ? 'animate-spin' : ''}`} />
                    <span className="px-3 text-xs font-semibold text-center">{statusLabel}</span>
                  </div>
                )}
              </div>
              <div className="px-3 py-2.5">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="truncate text-xs font-semibold text-gray-700">
                    {getModelDisplayName(item.modelId) || 'AI 生图'}
                  </span>
                  <time className="shrink-0 text-[10px] text-gray-400">
                    {formatHistoryDate(item.createdAt)}
                  </time>
                </div>
                {!complete && item.reasonCode && (
                  <p className="mt-1 text-[10px] text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                    {t(`gen_reason_${item.reasonCode}`, item.reasonCode)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 加载更多 */}
      {hasMore && (
        <div className="flex justify-center pb-6 pt-2">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-gray-600 bg-white cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-down-line text-sm"></i>
            <span>{loadingMore ? t('profile_loading') : t('profile_loadMore')}</span>
          </button>
        </div>
      )}

      {/* 图片预览弹层 */}
      {previewItem && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col overflow-hidden" onClick={() => setPreviewItem(null)}>
          {/* 顶栏 */}
          <div className="shrink-0 flex items-center justify-between px-4 pb-2" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}>
            <span className="text-white/50 text-xs font-medium">{formatHistoryDate(previewItem.createdAt)}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setPreviewItem(null); }}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm cursor-pointer active:scale-90 transition-transform"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>

          {/* 图片区 */}
          <div className="shrink-0 flex-1 flex items-center justify-center px-4 py-2 relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewItem.imageUrl || ''}
              alt={previewItem.prompt}
              className={`max-h-[52vh] max-w-full rounded-2xl object-contain transition-opacity duration-500 ${previewImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setPreviewImageLoaded(true)}
              onError={() => setPreviewImageLoaded(true)}
            />
            {!previewImageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* 底部操作面板 */}
          <div className="shrink-0 bg-black/70 backdrop-blur-2xl rounded-t-3xl px-5 pt-5 pb-24 overflow-y-auto max-h-[48vh]" onClick={(e) => e.stopPropagation()}>
            {/* 描述 + 复制 */}
            <div className="flex items-start gap-3 mb-3">
              <p className="text-white/90 text-sm leading-relaxed flex-1 line-clamp-2">{previewItem.prompt}</p>
              <button
                onClick={() => handleCopyPrompt(previewItem.prompt)}
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 cursor-pointer active:scale-90 transition-transform"
              >
                <i className="ri-file-copy-line text-white/60 text-sm"></i>
              </button>
            </div>

            {/* 元数据标签 */}
            <div className="flex items-center gap-2 mb-5">
              {previewItem.modelId && (
                <span
                  data-testid="history-preview-model"
                  className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/70 font-medium"
                >
                  {getModelDisplayName(previewItem.modelId)}
                </span>
              )}
              {previewItem.styleName.trim() && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/70 font-medium">{previewItem.styleName}</span>
              )}
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/70">{previewItem.ratio}</span>
              <span className="text-xs text-white/40 ml-auto">-{previewItem.pointsCost} {t('profile_pointsBalance')}</span>
            </div>

            {/* 主操作按钮 */}
            <div className="flex gap-3 mb-5">
              <button
                onClick={() => navigate(`/generate?prompt=${encodeURIComponent(previewItem.prompt)}&styleId=${encodeURIComponent(previewItem.styleName)}&ratio=${encodeURIComponent(previewItem.ratio)}`)}
                className="flex-1 h-12 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                <i className="ri-magic-line text-base"></i>
                <span>{t('profile_reuseParams')}</span>
              </button>
              <button
                onClick={() => navigate(`/generate?prompt=${encodeURIComponent(previewItem.prompt)}`)}
                className="flex-1 h-12 rounded-xl bg-white/10 text-white text-sm font-semibold flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
              >
                <i className="ri-refresh-line text-base"></i>
                <span>{t('profile_regenerate')}</span>
              </button>
            </div>

            {/* 分隔线 */}
            <div className="h-px bg-white/10 mb-4" />

            {/* 次级操作图标行 */}
            <div className="flex items-center justify-around px-2">
              <button
                onClick={(e) => handleToggleFavorite(previewItem.id, favoritedMap[previewItem.id] ?? previewItem.favorited, e)}
                className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform"
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${(favoritedMap[previewItem.id] ?? previewItem.favorited) ? 'bg-pink-500/20' : 'bg-white/10'}`}>
                  <i className={`${(favoritedMap[previewItem.id] ?? previewItem.favorited) ? 'ri-star-fill text-pink-400' : 'ri-star-line text-white/70'} text-base`}></i>
                </div>
                <span className={`text-[10px] font-medium ${(favoritedMap[previewItem.id] ?? previewItem.favorited) ? 'text-pink-400' : 'text-white/50'}`}>
                  {(favoritedMap[previewItem.id] ?? previewItem.favorited) ? t('profile_favorited') : t('profile_collect')}
                </span>
              </button>

              <button
                onClick={() => setShareOpen(true)}
                className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                  <i className="ri-share-forward-line text-white/70 text-base"></i>
                </div>
                <span className="text-[10px] text-white/50 font-medium">{t('profile_share')}</span>
              </button>

              <button
                onClick={() => { if (previewItem.imageUrl) handleSaveToAlbum(previewItem.imageUrl, previewItem.id); }}
                disabled={savingId === previewItem.id}
                className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform disabled:opacity-50"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                  {savingId === previewItem.id
                    ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <i className="ri-download-line text-white/70 text-base"></i>
                  }
                </div>
                <span className="text-[10px] text-white/50 font-medium">{t('profile_save')}</span>
              </button>

              <button
                onClick={() => { if (window.confirm(t('profile_deleteConfirm'))) { void handleDeleteItem(previewItem.id); } }}
                className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform"
              >
                <div className="w-11 h-11 rounded-full bg-red-500/15 flex items-center justify-center">
                  <i className="ri-delete-bin-line text-red-400/80 text-base"></i>
                </div>
                <span className="text-[10px] text-red-400/60 font-medium">{t('profile_delete')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 分享底部弹层 */}
      {previewItem && (
        <ShareSheet
          isOpen={shareOpen}
          imageUrl={previewItem.imageUrl || ''}
          prompt={previewItem.prompt}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* 提示 toast */}
      {(copyTip || saveTip || deleteTip) && (
        <div className="fixed inset-x-0 bottom-20 z-[120] flex justify-center px-5 pointer-events-none">
          <div className="bg-gray-900/90 text-white px-5 py-3 rounded-2xl flex items-center gap-2.5 shadow-lg backdrop-blur-sm">
            {copyTip && (
              <>
                <i className="ri-check-line text-green-400 text-lg" />
                <span className="text-sm font-medium">{t('profile_promptCopied')}</span>
              </>
            )}
            {saveTip === 'saving' && (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-sm font-medium">{t('gen_saving')}</span>
              </>
            )}
            {saveTip === 'done' && (
              <>
                <i className="ri-check-line text-green-400 text-lg" />
                <span className="text-sm font-medium">{t('gen_savedToAlbum')}</span>
              </>
            )}
            {saveTip === 'failed' && (
              <>
                <i className="ri-error-warning-line text-red-300 text-lg" />
                <span className="text-sm font-medium">{t('gen_saveFailed')}</span>
              </>
            )}
            {deleteTip === 'failed' && (
              <>
                <i className="ri-error-warning-line text-red-300 text-lg" />
                <span className="text-sm font-medium">{t('profile_deleteFailed')}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
